import { useEffect, useMemo, useState } from 'react'

import { resolveLiveEventsPickerMaxDate } from '../utils/utils'

type UseLiveEventsPickerMaxDateParams = {
  isInternalUser: boolean
  maxTimeFromEvents: Date
  maxTrackingEnded: number
  floorToToday?: boolean
  /** Whether any events visible to the current user are loaded (after the non-internal future-event cutoff). */
  hasVisibleEvents: boolean
  isFetching: boolean
  /** Identity of the current selection (e.g. market + game ids). Changing it resets the retained max. */
  resetKey: string
}

/**
 * Resolves the maximum selectable date for the live events date range picker, retaining the
 * largest value observed for the current selection (high-water mark).
 *
 * The picker max is derived from the events currently loaded, but the fetch window follows the
 * selected date range. When the user moves the range far into the past, the latest events drop
 * out of the loaded window and a naive computation would shrink the picker max to the end of
 * that past window, trapping the user in the past (GR-2964). Retaining the high-water mark keeps
 * the max at the last event end observed for this selection.
 *
 * The retained value is only updated from meaningful data (visible events loaded, not fetching),
 * so the today-fallback used for empty/loading/future-only states can never inflate the max
 * beyond the last real event, and refetches cannot shrink it. Retention is committed in an
 * effect so renders stay pure (safe with StrictMode/concurrent rendering).
 */
export const useLiveEventsPickerMaxDate = ({
  isInternalUser,
  maxTimeFromEvents,
  maxTrackingEnded,
  floorToToday = false,
  hasVisibleEvents,
  isFetching,
  resetKey,
}: UseLiveEventsPickerMaxDateParams): Date => {
  const [highWater, setHighWater] = useState<{ key: string; value: number }>()
  const key = `${resetKey}|${isInternalUser}`

  const resolvedTime = resolveLiveEventsPickerMaxDate({ isInternalUser, maxTimeFromEvents, maxTrackingEnded, floorToToday }).getTime()
  const isMeaningful = hasVisibleEvents && !isFetching
  const retained = highWater?.key === key ? highWater.value : undefined

  useEffect(() => {
    if (isMeaningful && (retained === undefined || resolvedTime > retained)) {
      setHighWater({ key, value: resolvedTime })
    }
  }, [key, isMeaningful, resolvedTime, retained])

  const value = retained === undefined ? resolvedTime : isMeaningful ? Math.max(retained, resolvedTime) : retained

  return useMemo(() => new Date(value), [value])
}
